import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Users, MapPin, Phone, Mail, Search, Plus, Edit2, UserX } from 'lucide-react';
import TeamMemberDialog from '../components/TeamMemberDialog';

export default function Team({ currentUser }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
        credentials: 'include'
      });
      const data = await response.json();
      setTeamMembers(data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      'back_office_manager': 'Back Office Manager',
      'customer_care_manager': 'Customer Care Manager',
      'account_manager': 'Account Manager',
      'loan_officer': 'Loan Officer',
      'operations_manager': 'Operations Manager',
      'sales_person': 'Sales',
      'pgvcl_work': 'PGVCL Work',
      'service_work': 'Service Work',
      'sales_pgvcl_loan': 'Sales/PGVCL/Loan'
    };
    return roleMap[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    if (role.includes('manager')) return 'bg-purple-100 text-purple-800';
    if (role.includes('sales')) return 'bg-blue-100 text-blue-800';
    if (role.includes('pgvcl')) return 'bg-green-100 text-green-800';
    if (role.includes('loan') || role.includes('account')) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Group team members
  const backOfficeStaff = teamMembers.filter(m => !m.branch);
  const branchStaff = teamMembers.filter(m => m.branch);
  
  // Filter based on search and branch
  const filteredBackOffice = backOfficeStaff.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredBranchStaff = branchStaff.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBranch = selectedBranch === 'all' || member.branch === selectedBranch;
    return matchesSearch && matchesBranch;
  });

  // Group branch staff by branch
  const staffByBranch = {};
  filteredBranchStaff.forEach(member => {
    if (!staffByBranch[member.branch]) {
      staffByBranch[member.branch] = [];
    }
    staffByBranch[member.branch].push(member);
  });

  const TeamMemberCard = ({ member }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-lg">
            {getInitials(member.name)}
          </div>
          
          {/* Details */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">{member.name}</h3>
                {member.branch && (
                  <Badge variant="outline" className="text-xs">
                    <MapPin className="w-3 h-3 mr-1" />
                    {member.branch}
                  </Badge>
                )}
                {!member.branch && (
                  <Badge variant="outline" className="text-xs bg-purple-50">
                    All Branches
                  </Badge>
                )}
              </div>
              {!currentUser?.branch && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingMember(member);
                    setShowDialog(true);
                  }}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            <Badge className={`${getRoleBadgeColor(member.role)} mb-3`}>
              {getRoleDisplay(member.role)}
            </Badge>
            
            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {member.email}
              </div>
              {member.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {member.phone}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Team Management</h1>
          <p className="text-muted-foreground mt-1">
            Solar Technology team members across Gujarat
          </p>
        </div>
        {!currentUser?.branch && (
          <Button onClick={() => { setEditingMember(null); setShowDialog(true); }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Team Member
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{teamMembers.length}</div>
              <p className="text-sm text-muted-foreground">Total Team Members</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{backOfficeStaff.length}</div>
              <p className="text-sm text-muted-foreground">Back Office Staff</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{branchStaff.length}</div>
              <p className="text-sm text-muted-foreground">Branch Staff</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={selectedBranch}
          onChange={(e) => setSelectedBranch(e.target.value)}
          className="border rounded-md px-4 py-2 min-w-[200px]"
        >
          <option value="all">All Branches</option>
          <option value="Rajkot">Rajkot</option>
          <option value="Jamnagar">Jamnagar</option>
          <option value="Porbandar">Porbandar</option>
          <option value="Junagadh">Junagadh</option>
          <option value="Morbi">Morbi</option>
          <option value="Khambhaliya">Khambhaliya</option>
        </select>
      </div>

      {/* Back Office Staff */}
      {filteredBackOffice.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Users className="w-6 h-6" />
            Back Office Staff
            <Badge variant="outline">{filteredBackOffice.length}</Badge>
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredBackOffice.map(member => (
              <TeamMemberCard key={member.user_id} member={member} />
            ))}
          </div>
        </div>
      )}

      {/* Branch Staff */}
      {Object.keys(staffByBranch).length > 0 && (
        <div className="space-y-6">
          {Object.entries(staffByBranch).sort().map(([branch, members]) => (
            <div key={branch}>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <MapPin className="w-6 h-6" />
                {branch}
                <Badge variant="outline">{members.length}</Badge>
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {members.map(member => (
                  <TeamMemberCard key={member.user_id} member={member} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {filteredBackOffice.length === 0 && Object.keys(staffByBranch).length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">No team members found</p>
          </CardContent>
        </Card>
      )}

      {/* Team Member Dialog */}
      {showDialog && (
        <TeamMemberDialog
          member={editingMember}
          onSave={fetchTeamMembers}
          onClose={() => { setShowDialog(false); setEditingMember(null); }}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}
